/**
 * Public Trust Center Page
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, Lock, X, Upload, FileCheck, Loader2, FileText, Shield } from 'lucide-react';
import { Badge, CATEGORIES } from '../components/ui-custom/ComplianceUI';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export default function TrustCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState(null);
  const [showNDAModal, setShowNDAModal] = useState(false);
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
  const [ndaForm, setNdaForm] = useState({ contact_name: '', contact_email: '', company_name: '', reason: '' });
  const [questionnaireForm, setQuestionnaireForm] = useState({ contact_name: '', contact_email: '', company_name: '' });
  const [questionnaireFile, setQuestionnaireFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [trustRes, statusRes] = await Promise.all([
          fetch(`${API_URL}/api/trust-center`),
          fetch(`${API_URL}/api/system/status`),
        ]);

        if (trustRes.ok) {
          setData(await trustRes.json());
        }
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          console.log('System status loaded:', statusData);
          setSystemStatus(statusData);
        } else {
          console.error('Failed to fetch system status:', statusRes.status);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleNDASubmit = async () => {
    if (!ndaForm.contact_email || !ndaForm.company_name || !ndaForm.contact_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/trust-center/nda-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ndaForm),
      });
      if (response.ok) {
        toast.success('Request submitted! We will contact you within 2 business days.');
        setShowNDAModal(false);
        setNdaForm({ contact_name: '', contact_email: '', company_name: '', reason: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit request');
      }
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuestionnaireSubmit = async () => {
    if (!questionnaireFile || !questionnaireForm.contact_email || !questionnaireForm.company_name || !questionnaireForm.contact_name) {
      toast.error('Please fill in all required fields and upload a file');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/trust-center/security-questionnaire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...questionnaireForm,
          file_name: questionnaireFile.name,
          file_size: questionnaireFile.size,
        }),
      });
      if (response.ok) {
        toast.success('Questionnaire submitted! We will complete it within 5 business days.');
        setShowQuestionnaireModal(false);
        setQuestionnaireForm({ contact_name: '', contact_email: '', company_name: '' });
        setQuestionnaireFile(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit questionnaire');
      }
    } catch (error) {
      toast.error('Failed to submit questionnaire');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setQuestionnaireFile(e.dataTransfer.files[0]);
    }
  };

  if (loading) {
    return (
      <div className="trust-center-public loading">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="trust-center-public">
      <header className="trust-header">
        <div className="trust-header-content">
          <div className="trust-logo">
            <img src="/eyvo-logo.png" alt="Eyvo" className="trust-logo-img" />
            <span>Trust </span>
            <span 
              className="db-tooltip-trigger"
              data-tooltip={
                !systemStatus ? 'Loading...' :
                systemStatus?.database?.connected 
                  ? `${systemStatus?.database?.host} / ${systemStatus?.database?.name}` 
                  : 'Disconnected'
              }
            >Center</span>
          </div>
          <Link to="/login" className="staff-login">
            <Lock size={16} /> Staff Login
          </Link>
        </div>
      </header>

      <section className="trust-hero-section">
        <Badge color="#2563eb">Self-Assessed SOC 2 Controls</Badge>
        <h1>Security & Compliance at Eyvo Procurement</h1>
        <p>We are committed to maintaining the highest standards of security and privacy for our customers' data. Explore our compliance posture and certifications.</p>
        <div className="hero-buttons">
          <Link to="/security-overview" className="btn-primary">
            <FileText size={18} /> Security Overview
          </Link>
        </div>

        <div className="hero-portal-access">
          <div className="hero-portal-icon">
            <Lock size={28} color="#0891b2" />
          </div>
          <div className="hero-portal-body">
            <h3>Request Client Portal Access</h3>
            <p>Create your account and pick a password. Once approved, you can log in and see whatever your account is granted — Policies, CAIQ, Accessibility docs, and more. 2FA on first sign-in.</p>
          </div>
          <Link
            to="/request-access"
            className="btn-primary hero-portal-cta"
            data-testid="public-request-portal-access-btn"
            style={{ textDecoration: 'none' }}
          >
            Request Access
          </Link>
        </div>
      </section>

      <section id="compliance" className="trust-compliance">
        <h2>Compliance Overview</h2>
        <div className="compliance-grid">
          {data?.categories?.map((cat) => {
            return (
              <div key={cat.category} className="compliance-card">
                <div className={`compliance-circle ${cat.compliance_pct >= 80 ? 'good' : cat.compliance_pct >= 60 ? 'warning' : 'bad'}`}>
                  <span>{cat.compliance_pct}%</span>
                </div>
                <h3>{cat.display_name}</h3>
                <p>{cat.passing_controls}/{cat.total_controls} controls</p>
              </div>
            );
          }) || Object.entries(CATEGORIES).map(([k, meta]) => {
            return (
              <div key={k} className="compliance-card">
                <div className="compliance-circle good">
                  <Shield size={24} />
                </div>
                <h3>{meta.label}</h3>
                <p>Monitored</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="trust-certifications">
        <h2>Compliance Frameworks (Self-Assessed)</h2>
        <div className="cert-grid">
          {[
            { name: 'SOC 2', status: 'Self-Assessed' },
            { name: 'NIST 800-30', status: 'Self-Assessed' },
            { name: 'GDPR', status: 'Compliant' },
            { name: 'CCPA', status: 'Compliant' }
          ].map((cert) => (
            <div key={cert.name} className="cert-card">
              <ShieldCheck size={24} color="#2563eb" />
              <span>{cert.name}</span>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>{cert.status}</small>
            </div>
          ))}
        </div>
      </section>

      {/* Request & Questionnaire Section */}
      <section className="trust-request-section">
        <h2>Request Documentation</h2>
        <div className="request-grid request-grid-2col">
          <div className="request-card">
            <FileText size={32} color="#2563eb" />
            <h3>Request Detailed Report</h3>
            <p>Submit an NDA request to access detailed compliance documentation, including CAIQ responses and security architecture details.</p>
            <button className="btn-primary" onClick={() => setShowNDAModal(true)} data-testid="public-request-access-btn">
              Request NDA
            </button>
          </div>
          <div className="request-card">
            <Upload size={32} color="#059669" />
            <h3>Submit Security Questionnaire</h3>
            <p>Submit your security questionnaire for our team to complete. Standard turnaround is 5 business days.</p>
            <button className="btn-primary" onClick={() => setShowQuestionnaireModal(true)} data-testid="public-submit-questionnaire-btn">
              Submit Questionnaire
            </button>
          </div>
        </div>
      </section>

      <footer className="trust-footer">
        <div className="trust-footer-content">
          <div className="footer-logo">
            <ShieldCheck size={20} color="#60a5fa" />
            <span>Eyvo Procurement</span>
          </div>
          <p>© {new Date().getFullYear()} Eyvo, Inc. All rights reserved.</p>
        </div>
        
        {/* System Status */}
        <div className="footer-system-status">
          <div className="footer-status-item">
            <span className="footer-status-label">Version</span>
            <span className="footer-status-value">{systemStatus?.version || '1.3'}</span>
          </div>
          <div className="footer-status-divider" />
          <div className="footer-status-item">
            <span className="footer-status-label">Released</span>
            <span className="footer-status-value">{systemStatus?.release_date || 'April 29, 2026'}</span>
          </div>
          <div className="footer-status-divider" />
          <div className="footer-status-item">
            <span className={`footer-status-indicator ${systemStatus?.database?.connected ? 'connected' : 'disconnected'}`} />
            <span className="footer-status-value">
              {systemStatus?.database?.connected ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>
      </footer>

      {/* NDA Request Modal */}
      {showNDAModal && (
        <div className="modal-overlay" onClick={() => setShowNDAModal(false)}>
          <div className="modal-content public-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Request Detailed Compliance Report</h2>
              <button className="modal-close" onClick={() => setShowNDAModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Submit your information below. We will send an NDA for signature and provide access to detailed compliance documentation within 2 business days.
              </p>
              <div className="form-group">
                <label>Your Name *</label>
                <input
                  type="text"
                  value={ndaForm.contact_name}
                  onChange={(e) => setNdaForm(f => ({ ...f, contact_name: e.target.value }))}
                  placeholder="John Smith"
                  data-testid="public-nda-name"
                />
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={ndaForm.contact_email}
                  onChange={(e) => setNdaForm(f => ({ ...f, contact_email: e.target.value }))}
                  placeholder="john@company.com"
                  data-testid="public-nda-email"
                />
              </div>
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={ndaForm.company_name}
                  onChange={(e) => setNdaForm(f => ({ ...f, company_name: e.target.value }))}
                  placeholder="Acme Corporation"
                  data-testid="public-nda-company"
                />
              </div>
              <div className="form-group">
                <label>Reason for Request</label>
                <textarea
                  value={ndaForm.reason}
                  onChange={(e) => setNdaForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g., Vendor security assessment, RFP response..."
                  rows={3}
                  data-testid="public-nda-reason"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowNDAModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleNDASubmit} disabled={submitting} data-testid="public-submit-nda-btn">
                {submitting ? <Loader2 className="spin" size={16} /> : null}
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Questionnaire Upload Modal */}
      {showQuestionnaireModal && (
        <div className="modal-overlay" onClick={() => setShowQuestionnaireModal(false)}>
          <div className="modal-content public-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Submit Security Questionnaire</h2>
              <button className="modal-close" onClick={() => setShowQuestionnaireModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Upload your security questionnaire (PDF, CSV, or Excel). We will complete it using our existing compliance documentation and return within 5 business days.
              </p>
              <div className="form-group">
                <label>Your Name *</label>
                <input
                  type="text"
                  value={questionnaireForm.contact_name}
                  onChange={(e) => setQuestionnaireForm(f => ({ ...f, contact_name: e.target.value }))}
                  placeholder="John Smith"
                  data-testid="public-quest-name"
                />
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={questionnaireForm.contact_email}
                  onChange={(e) => setQuestionnaireForm(f => ({ ...f, contact_email: e.target.value }))}
                  placeholder="john@company.com"
                  data-testid="public-quest-email"
                />
              </div>
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={questionnaireForm.company_name}
                  onChange={(e) => setQuestionnaireForm(f => ({ ...f, company_name: e.target.value }))}
                  placeholder="Acme Corporation"
                  data-testid="public-quest-company"
                />
              </div>
              <div className="form-group">
                <label>Questionnaire File *</label>
                <div
                  className={`dropzone ${dragActive ? 'active' : ''} ${questionnaireFile ? 'has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('public-questionnaire-file').click()}
                  data-testid="public-questionnaire-dropzone"
                >
                  <input
                    type="file"
                    id="public-questionnaire-file"
                    accept=".pdf,.csv,.xlsx,.xls,.json,.doc,.docx"
                    style={{ display: 'none' }}
                    onChange={(e) => setQuestionnaireFile(e.target.files[0])}
                  />
                  {questionnaireFile ? (
                    <>
                      <FileCheck size={32} color="#059669" />
                      <div className="dropzone-text">{questionnaireFile.name}</div>
                      <div className="dropzone-hint">Click to change file</div>
                    </>
                  ) : (
                    <>
                      <Upload size={32} color="#6b7280" />
                      <div className="dropzone-text">Drag & drop your questionnaire here</div>
                      <div className="dropzone-hint">or click to browse (PDF, CSV, Excel, Word)</div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowQuestionnaireModal(false)}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={handleQuestionnaireSubmit} 
                disabled={submitting || !questionnaireFile}
                data-testid="public-upload-questionnaire-btn"
              >
                {submitting ? <Loader2 className="spin" size={16} /> : <Upload size={16} />}
                {submitting ? 'Submitting...' : 'Submit Questionnaire'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
